import { memo, useState, useMemo, useCallback } from 'react';
import { motion, useMotionValue, useTransform, AnimationControls, PanInfo } from 'framer-motion';
import { cn } from '../../lib/utils';
import { CardComponent, type Card } from './Card';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useHapticFeedback } from '../../hooks/useHapticFeedback';

interface CarouselContentProps {
  handleClick: (card: Card, index: number) => void;
  controls: AnimationControls;
  cards: Card[];
  isCarouselActive: boolean;
  defaultPremium: boolean;
}

export const CarouselContent = memo<CarouselContentProps>(
  ({
    handleClick,
    controls,
    cards,
    isCarouselActive,
    defaultPremium
  }) => {
    const { impactOccurred, notificationOccurred } = useHapticFeedback();
    const isScreenSizeSm = useMediaQuery("(max-width: 640px)");
    const cylinderWidth = isScreenSizeSm ? 1150 : 1850;
    const faceCount = cards.length;
    
    const { faceWidth, radius, anglePerCard } = useMemo(() => ({
      faceWidth: cylinderWidth / faceCount,
      radius: cylinderWidth / (2 * Math.PI),
      anglePerCard: 360 / faceCount
    }), [cylinderWidth, faceCount]);
    
    const rotation = useMotionValue(0);

    const transform = useTransform(
      rotation,
      (value) => `rotateY(${value}deg)`
    );

    const [startX, setStartX] = useState(0);
    const [isDragging, setIsDragging] = useState(false);

    const handleDragStart = useCallback((_: never, info: PanInfo) => {
      if (!isCarouselActive) return;
      setStartX(info.point.x);
      setIsDragging(true);
      impactOccurred('light');
    }, [isCarouselActive, impactOccurred]);

    const handleDragEnd = useCallback((_: never, info: PanInfo) => {
      if (!isCarouselActive) return;

      const swipeDistance = info.point.x - startX;
      const SWIPE_THRESHOLD = 30;
      
      setIsDragging(false);
      
      if (Math.abs(swipeDistance) > SWIPE_THRESHOLD) {
        const currentValue = rotation.get();
        const direction = Math.sign(swipeDistance);
        const newRotation = currentValue + (direction * anglePerCard);
        
        notificationOccurred('success');
        
        controls.start({
          rotateY: newRotation,
          transition: {
            type: "spring",
            stiffness: 200,
            damping: 25,
            restDelta: 0.5,
          },
        });
      }
    }, [isCarouselActive, startX, rotation, anglePerCard, controls, notificationOccurred]);

    return (
      <div 
        className="relative w-full h-[400px] flex items-center justify-center bg-tg-bg -mt-4 max-[640px]:h-[290px]"
        style={{
          perspective: '2000px',
          transformStyle: 'preserve-3d'
        }}
      >
        <motion.div
          drag={isCarouselActive ? "x" : false}
          dragElastic={0}
          dragConstraints={{ left: 0, right: 0 }}
          className={cn(
            'relative h-full w-full flex items-center justify-center',
            'cursor-grab m-0 p-0',
            'active:cursor-grabbing'
          )}
          style={{
            transform,
            rotateY: rotation,
            width: cylinderWidth,
            transformStyle: "preserve-3d",
            willChange: isDragging ? 'transform' : 'auto',
          }}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          animate={controls}
        >
          {cards.map((card, i) => (
            <CardComponent
              key={card.id}
              card={card}
              index={i}
              faceCount={faceCount}
              faceWidth={faceWidth}
              radius={radius}
              isPremium={card.isPremium ?? defaultPremium}
              onClick={handleClick}
            />
          ))}
        </motion.div>
      </div>
    );
  }
);

CarouselContent.displayName = 'CarouselContent';

